import { useState, useDeferredValue, useMemo, Fragment } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  Collection,
  Badge,
  MenuSection,
  MenuItem,
  SearchField,
  SubmenuTrigger,
  DialogTrigger,
  Popover,
  TagGroup,
  Tag,
  ActionButton,
  MenuTrigger,
  Menu,
  Text,
  RangeCalendar,
  Switch,
  Picker,
  PickerItem,
  Divider,
} from "@react-spectrum/s2";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import { useDateFormatter } from "react-aria";
import LinkOutIcon from "icon:@app/assets/S2_LinkOutSize100.svg";
import AddCircleIcon from "@react-spectrum/s2/icons/AddCircle";
import ExportIcon from "@react-spectrum/s2/icons/Export";
import DownloadIcon from "@react-spectrum/s2/icons/Download";

import {
  useAdminStats,
  useAdminUsers,
  useAddUserRole,
  useDownloadVideosReport,
} from "@app/hooks/admin";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

const columns = [
  { name: "Email", id: "userID", isRowHeader: true, allowsSorting: true },
  { name: "Role", id: "roles", allowsSorting: true, width: 152 },
  {
    name: "Videos\u00a0\u00a0\u00a0\u00a0",
    id: "videos",
    align: "end",
    allowsSorting: true,
    width: 120,
  },
  // { name: "", id: "actions", width: 25, minWidth: 35 },
];

const ASSIGNABLE_ROLES = [{ id: "foundry", label: "Foundry" }];

const Stat = ({ label, value, size = "L" }) => (
  <div>
    <div
      className={style({
        font: {
          size: {
            M: { md: "detail-sm", default: "detail-sm" },
            L: { md: "detail", default: "detail-sm" },
            default: { md: "detail", default: "detail-sm" },
          },
        },
      })({ size })}
    >
      {label}
    </div>
    <div
      className={style({
        font: {
          size: {
            M: { md: "heading-xl", default: "heading" },
            L: { md: "heading-3xl", default: "heading-xl" },
            default: { md: "heading-3xl", default: "heading-xl" },
          },
        },
        lineHeight: "[1]",
      })({ size })}
    >
      {value}
    </div>
  </div>
);

const datePresets = {
  "FY26 Q1": {
    start: parseDate("2025-11-29"),
    end: parseDate("2026-02-27"),
  },
  "FY26 Q2": {
    start: parseDate("2026-02-28"),
    end: parseDate("2026-05-29"),
  },
  "FY26 Q3": {
    start: parseDate("2026-05-30"),
    end: parseDate("2026-08-28"),
  },
  "FY26 Q4": {
    start: parseDate("2026-08-29"),
    end: parseDate("2026-11-27"),
  },
  "FY27 Q1": {
    start: parseDate("2026-11-28"),
    end: parseDate("2027-03-05"),
  },
  "FY27 Q2": {
    start: parseDate("2027-03-06"),
    end: parseDate("2027-06-04"),
  },
  "FY27 Q3": {
    start: parseDate("2027-06-05"),
    end: parseDate("2027-09-03"),
  },
  "FY27 Q4": {
    start: parseDate("2027-09-04"),
    end: parseDate("2027-12-03"),
  },
};

const currentQuarter = Object.entries(datePresets)
  .find(([key, { start, end }]) => {
    const now = new Date();
    return (
      now >= start.toDate(getLocalTimeZone()) &&
      now <= end.toDate(getLocalTimeZone())
    );
  })
  ?.reduce(
    (acc, val) =>
      typeof val === "string" ? { ...acc, key: val } : { ...acc, ...val },
    {},
  );

export const AdminUsers = () => {
  useBreadcrumbs([
    { label: "Admin", href: "/admin" },
    { label: "Users" },
  ]);

  const [isTotalRange, setTotalRange] = useState(false);
  const [range, setRange] = useState({
    start: currentQuarter?.start ?? datePresets["FY26 Q2"].start,
    end: currentQuarter?.end ?? datePresets["FY26 Q2"].end,
  });

  const reportFilters = isTotalRange
    ? {}
    : { fromDate: range.start.toString(), toDate: range.end.toString() };

  const adminStats = useAdminStats(reportFilters);
  const adminUsers = useAdminUsers();
  const addUserRoleMutation = useAddUserRole();
  const downloadReport = useDownloadVideosReport();

  const [sortDescriptor, setSortDescriptor] = useState(null);
  const [filter, setFilter] = useState({});
  const deferredFilter = useDeferredValue(filter);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);

  const formatter = useDateFormatter({ dateStyle: "long" });

  const rows = useMemo(() => {
    let sortedRows = [...(adminUsers.data ?? [])];

    if (sortDescriptor) {
      sortedRows =
        adminUsers.data?.toSorted((a, b) => {
          let [first, second] = [
            a[sortDescriptor.column],
            b[sortDescriptor.column],
          ].map((field) => {
            if (typeof field === "string") {
              field = field.toLowerCase();
            } else if (Array.isArray(field)) {
              field = field.join(" ").toLowerCase();
            }

            return field;
          });

          if (!second) return -1;

          let cmp = first < second ? -1 : 1;
          if (sortDescriptor.direction === "descending") {
            cmp = -cmp;
          }
          return cmp;
        }) ?? [];
    }

    let filteredRows = [...sortedRows];

    if (Object.keys(filter).length > 0) {
      filteredRows = sortedRows.filter((row) => {
        return Object.entries(deferredFilter).every(([key, value]) => {
          if (!value) return true;
          const field = row[key];
          if (typeof field === "string") {
            return field.toLowerCase().includes(value.toLowerCase());
          } else if (Array.isArray(field)) {
            return field.some((item) =>
              item.toLowerCase().includes(value.toLowerCase()),
            );
          }
          return false;
        });
      });
    }

    return filteredRows;
  }, [adminUsers.data, sortDescriptor, deferredFilter]);

  const [presetValue, setPresetValue] = useState(currentQuarter?.key ?? null);

  const quarterOptions = useMemo(() => {
    return Object.entries(datePresets).map(([key, { start, end }]) => ({
      key,
      label: `${formatter.formatRange(
        start.toDate(getLocalTimeZone()),
        end.toDate(getLocalTimeZone()),
      )}`,
      start,
      end,
    }));
  }, [formatter]);

  const handlePresetChange = (key) => {
    const preset = datePresets[key];
    if (preset) {
      setRange({ start: preset.start, end: preset.end });
      setPresetValue(key);
      setTotalRange(false);
    }
  };

  const handleRangeChange = (newRange) => {
    setRange(newRange);
    setPresetValue(null);
    setTotalRange(false);
  };

  const handleTotalRangeChange = (isTotal) => {
    setTotalRange(isTotal);
    if (isTotal) {
      setPresetValue(null);
    } else if (currentQuarter) {
      setRange({ start: currentQuarter.start, end: currentQuarter.end });
      setPresetValue(currentQuarter.key);
    }
  };

  return (
    <div
      className={style({
        display: "grid",
        gridTemplateAreas: {
          md: ["header header", "tableFilters statsFilters", "table stats"],
          default: ["header", "tableFilters", "table", "stats"],
        },
        gridTemplateColumns: {
          md: [460, "minmax(0, 1fr)"],
          default: ["minmax(0, 1fr)"],
        },
        gridTemplateRows: {
          md: ["auto", "auto", "minmax(0, fit-content)"],
          default: ["auto", "auto", "50vh", "minmax(0, fit-content)"],
        },
        alignContent: "start",
        columnGap: 48,
        rowGap: 24,
        size: "full",
        maxHeight: { md: "calc(100dvh - 56px - 48px)" },
      })}
    >
      <div className={style({ gridArea: "header", font: "heading" })}>
        Users
      </div>
      <div className={style({ gridArea: "tableFilters" })}>
        {Object.values(deferredFilter).filter(Boolean).length > 0 && (
          <TagGroup
            label="Filters"
            size="S"
            onRemove={([key]) =>
              setFilter((old) => ({
                ...old,
                [key]: "",
              }))
            }
          >
            {Object.entries(deferredFilter).map(
              ([key, value]) =>
                !!value && (
                  <Tag id={key} key={key}>
                    {key}: {value}
                  </Tag>
                ),
            )}
          </TagGroup>
        )}
      </div>
      <TableView
        aria-label="All users"
        sortDescriptor={sortDescriptor ?? undefined}
        onSortChange={setSortDescriptor}
        renderEmptyState={() => "No users found"}
        overflowMode="wrap"
        styles={style({
          gridArea: "table",
          maxHeight: "full",
          height: { md: "unset", default: `min(${size(320)}, 50dvh)` },
        })}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <Column
              showDivider={column.showDivider}
              align={column.align}
              isRowHeader={column.isRowHeader}
              allowsSorting={column.allowsSorting}
              width={column.width}
              menuItems={
                column.id === "userID" && (
                  <>
                    <MenuSection>
                      <SubmenuTrigger>
                        <MenuItem>Filter</MenuItem>
                        <Popover>
                          <SearchField
                            placeholder={`Filter by ${column.name.toLowerCase()}`}
                            defaultValue={filter[column.id]}
                            onChange={(value) =>
                              setFilter((old) => ({
                                ...old,
                                [column.id]: value,
                              }))
                            }
                            autoFocus
                            styles={style({ width: 200 })}
                          />
                        </Popover>
                      </SubmenuTrigger>
                    </MenuSection>
                  </>
                )
              }
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        <TableBody>
          <Collection
            items={rows}
            dependencies={
              process.env.NODE_ENV === "development" ? [+new Date()] : []
            }
          >
            {(user) => {
              const userRoles = user.roles ?? [];
              const missingRoles = ASSIGNABLE_ROLES.filter(
                (r) => !userRoles.includes(r.id),
              );
              const canAddRole = missingRoles.length > 0;
              const showAddTrigger =
                canAddRole &&
                (hoveredUserId === user.userID ||
                  openMenuUserId === user.userID);
              const isSavingRole =
                addUserRoleMutation.isPending &&
                addUserRoleMutation.variables?.userID === user.userID;

              const rowPointerProps = {
                onPointerEnter: () => setHoveredUserId(user.userID),
                onPointerLeave: (e) => {
                  const tr = e.currentTarget?.closest?.("tr") ?? null;
                  if (
                    tr &&
                    e.relatedTarget instanceof Node &&
                    tr.contains(e.relatedTarget)
                  ) {
                    return;
                  }
                  setHoveredUserId((id) => (id === user.userID ? null : id));
                },
              };

              return (
                <Row id={user.userID} href={`/projects/user/${user.userID}`}>
                  <Cell {...rowPointerProps}>{user.userID}</Cell>
                  <Cell {...rowPointerProps}>
                    <div
                      className={style({
                        display: "flex",
                        gap: 4,
                        flexWrap: "wrap",
                        alignItems: "center",
                      })}
                    >
                      {user.roles?.map((role) => (
                        <span
                          key={role}
                          className={style({ display: "inline-flex" })}
                        >
                          <Badge size="S" variant="accent" fillStyle="subtle">
                            {role}
                          </Badge>
                        </span>
                      ))}
                      {canAddRole && (
                        <span
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          className={style({
                            display: "inline-flex",
                            alignItems: "center",
                            minWidth: 28,
                            justifyContent: "center",
                          })}
                        >
                          <MenuTrigger
                            isOpen={openMenuUserId === user.userID}
                            onOpenChange={(open) =>
                              setOpenMenuUserId(open ? user.userID : null)
                            }
                          >
                            <ActionButton
                              isQuiet
                              size="S"
                              aria-label={`Add role for ${user.userID}`}
                              isDisabled={isSavingRole}
                              UNSAFE_style={{
                                opacity: showAddTrigger ? 1 : 0,
                                pointerEvents: showAddTrigger ? "auto" : "none",
                                transition: "opacity 150ms ease",
                              }}
                            >
                              <AddCircleIcon />
                            </ActionButton>
                            <Menu
                              aria-label={`Add role for ${user.userID}`}
                              onAction={(key) => {
                                addUserRoleMutation.mutate({
                                  userID: user.userID,
                                  role: String(key),
                                });
                              }}
                            >
                              {missingRoles.map((r) => (
                                <MenuItem
                                  key={r.id}
                                  id={r.id}
                                  textValue={r.label}
                                  isDisabled={isSavingRole}
                                >
                                  <Text>{r.label}</Text>
                                </MenuItem>
                              ))}
                            </Menu>
                          </MenuTrigger>
                        </span>
                      )}
                    </div>
                  </Cell>
                  <Cell align="end">
                    {user.videos?.length ?? 0}
                    <LinkOutIcon styles={style({ size: 8, marginStart: 8 })} />
                  </Cell>
                </Row>
              );
            }}
          </Collection>
        </TableBody>
      </TableView>
      <div
        className={style({
          gridArea: "stats",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          overflowY: { md: "auto", default: "unset" },
        })}
      >
        <div className={style({ display: "flex", gap: 24, alignItems: "end" })}>
          <Stat label="Total users" value={adminStats.data?.totalUsers ?? 0} />
        </div>
        <div
          className={style({ display: "flex", gap: 8, alignItems: "center" })}
        >
          <Text styles={style({ font: "ui" })}>Date range:</Text>
          <DialogTrigger>
            <ActionButton isQuiet staticColor="auto">
              <Text>
                {isTotalRange
                  ? "All time"
                  : formatter.formatRange(
                      range.start.toDate(getLocalTimeZone()),
                      range.end.toDate(getLocalTimeZone()),
                    )}
              </Text>
            </ActionButton>
            <Popover>
              <div
                className={style({
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                })}
              >
                <RangeCalendar
                  value={range}
                  onChange={handleRangeChange}
                  visibleMonths={2}
                  selectionAlignment="end"
                  defaultFocusedValue={range.end}
                  isDisabled={isTotalRange}
                />
                <div
                  className={style({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  })}
                >
                  <Switch
                    isSelected={isTotalRange}
                    onChange={handleTotalRangeChange}
                  >
                    Show all time data
                  </Switch>
                  <Picker
                    label="Presets"
                    items={quarterOptions}
                    value={presetValue}
                    onChange={handlePresetChange}
                    labelPosition="side"
                    menuWidth={280}
                  >
                    {(item) => (
                      <PickerItem textValue={item.label}>
                        <Text slot="label">{item.key}</Text>
                        <Text slot="description">{item.label}</Text>
                      </PickerItem>
                    )}
                  </Picker>
                </div>
              </div>
            </Popover>
          </DialogTrigger>
        </div>

        <div
          className={style({ display: "flex", gap: 12, alignItems: "center" })}
        >
          <Text styles={style({ font: "title-xl" })}>Total</Text>
          <ActionButton
            isQuiet
            size="S"
            staticColor="auto"
            isDisabled={downloadReport.isPending}
            onPress={() => downloadReport.mutate(reportFilters)}
          >
            <DownloadIcon />
            <Text>
              {downloadReport.isPending ? "Preparing…" : "Download report"}
            </Text>
          </ActionButton>
        </div>
        <div className={style({ display: "flex", gap: 24, alignItems: "end" })}>
          <Stat
            label="Created videos"
            value={adminStats.data?.totalVideos ?? 0}
          />
          <Stat
            label={
              <>
                Created videos
                <br />
                <span className={style({ font: "detail-sm" })}>
                  (w/ account or DR)
                </span>
              </>
            }
            value={adminStats.data?.generatedVideos ?? 0}
          />
          <Stat
            label="# DR numbers"
            value={adminStats.data?.totalDrNumbers ?? 0}
          />
          <Stat
            label="# Account names"
            value={adminStats.data?.totalAccountNames ?? 0}
          />
        </div>
        {adminStats.data?.templateTypes?.map((template) => (
          <Fragment key={template.templateId}>
            <Divider size="M" />
            <div className={style({ font: "title" })}>
              <Text styles={style({ font: "detail" })}>Template:</Text>{" "}
              {template.templateId}
            </div>
            <div
              className={style({ display: "flex", gap: 24, alignItems: "end" })}
            >
              <Stat label="Created videos" value={template.total} size="M" />
              <Stat
                label={`(w/ account or DR)`}
                value={template.withDR}
                size="M"
              />
              <Stat
                label="# DR numbers"
                value={template.drNumbers ?? 0}
                size="M"
              />
              <Stat
                label="# Account names"
                value={template.accountNames ?? 0}
                size="M"
              />
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
};
